/*---------------Libraries-----------------*/
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "react-toastify";

/*---------------Routing & State-----------*/
import router from "../router/Index";
import { store } from "../stores/store";
import keycloak from "../../config/keycloak";

/*---------------Types - Customer----------*/
import {
  CustomerListFilter,
  CustomerListResponse,
  CustomerDetailResponse,
} from "../models/Customer";

import {
  LoginAuthWrapper,
  RefreshToken,
  RefreshTokenPayload,
  SystemUser,
  RoleMenuActionResponse,
  KamStatResponse,
} from "../models/SystemUser";

/*---------------Types - Orders------------*/
import {
  CustomerOrderStatsReponse,
  OrderResponse,
  ReorderPayload,
  ReorderResponse,
} from "../models/Order";

/*---------------Types - Cart--------------*/
import { CartDetails } from "../models/CartItems";
import {
  AddToCartReqParams,
  AddToCartRes,
  ClearCartReqParams,
  ExcelUploadResponse,
} from "../models/AddedCartItems";
import {
  CartQuantityValidationHeaders,
  CartQuantityValidationPayload,
  CartQuantityValidationResponse,
} from "../models/CartQuantityValidation";

/*---------------Types - Products----------*/
import { SearchProducts, AutocompleteResponse } from "../models/Products";

/*---------------Types - Dashboard---------*/
import {
  DailyMetricsResponse,
  DashboardMetricResponse,
  PreviousMonthMetricResponse,
  TopDashboardResponse,
} from "../models/Dashboard";
import { Login } from "../models/Authentication";
import { OrderDetailApiResponse } from "../models/OrderDetail";
import {
  GenerateOrderApiResponse,
  GenerateOrderPayload,
} from "../models/Order";
import {
  ApplicableCouponsApiResponse,
  VerifyCouponApiResponse,
} from "../models/Coupons";
import { GetAllDeliverySlotsApiResponse } from "../models/DeliverySlots";
import { AdminProfileApiResponse } from "../models/AdminProfile";
import { GetWareHouseResponse } from "../models/Warehouse";
import {
  QuickDeliveryConfigurationApiResponse,
  ApiResponse,
} from "../models/miscellaneous";
import {
  GetAllCitiesApiResponse,
  GetAllStateApiResponse,
  CustomerTypeResponse,
  AddOutletResponse,
  OutletByOrgResponse,
  UpdateOutletResponse,
  BusinessTypeResponse,
} from "../models/Onboarding";
import {
  LocationAutocompleteResponse,
  LocationDetailsResponse,
} from "../models/LocationSearch";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

const axiosPublic = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

// Queue for handling concurrent refresh requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axios.interceptors.request.use(async (config) => {
  if (keycloak.authenticated && keycloak.token) {
    try {
      // Refresh token if it is going to expire in the next 30 seconds
      await keycloak.updateToken(30);
      if (config.headers) {
        config.headers.Authorization = `Bearer ${keycloak.token}`;
      }
    } catch (error) {
      console.error("Failed to refresh token", error);
      keycloak.login();
    }
  } else {
    const token = store.commonStore.token;
    if (token && config.headers)
      config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle cases where response might be undefined
    if (!error.response) {
      return Promise.reject(error);
    }

    const { status } = error.response as AxiosResponse;

    switch (status) {
      case 400:
        toast.error("bad request");
        break;

      case 401: {
        // If Keycloak is authenticated, we handle 401 differently
        if (keycloak.authenticated) {
          toast.error("Session expired or unauthorized.");
          keycloak.logout();
          return Promise.reject(error);
        }

        // If already retried, clear auth and redirect
        if (originalRequest._retry) {
          store.commonStore.clearAuth();
          keycloak.login();
          return Promise.reject(error);
        }

        // If refresh is already in progress, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return axios(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const storedRefreshToken = localStorage.getItem("refreshToken");

        if (!storedRefreshToken) {
          isRefreshing = false;
          processQueue(error, null);
          store.commonStore.clearAuth();
          keycloak.login();
          return Promise.reject(error);
        }

        try {
          const prefresh: RefreshTokenPayload = {
            refreshToken: storedRefreshToken,
          };

          const currentToken = store.commonStore.token;
          const refreshResponse = await axiosPublic.post<RefreshToken>(
            "/api/admin/token_refresh",
            prefresh,
            {
              headers: currentToken
                ? { Authorization: `Bearer ${currentToken}` }
                : {},
            },
          );
          const refreshed: RefreshToken = refreshResponse.data;
          const newToken = refreshed?.data?.token;
          const newRefreshToken = refreshed?.data?.refreshToken;

          if (!newToken || !newRefreshToken) {
            throw new Error("Invalid refresh token response");
          }

          store.commonStore.setToken(newToken);
          store.commonStore.setRefreshToken(newRefreshToken);

          // Update the original request header
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          // Process queued requests
          processQueue(null, newToken);
          isRefreshing = false;

          // Retry the original request
          return axios(originalRequest);
        } catch (refreshError) {
          // Process queued requests with error
          processQueue(refreshError, null);
          isRefreshing = false;

          // Clear auth and redirect
          store.commonStore.clearAuth();
          keycloak.login();

          return Promise.reject(refreshError);
        }
      }

      case 403:
        toast.error("forbidden");
        break;

      case 404:
        toast.error(`API returned 404: ${error.config?.url ?? "URL NOT FOUND"}`);
        return Promise.reject(error);

      case 500:
        console.log(error);
        break;
    }

    return Promise.reject(error);
  },
);

const requests = {
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  post: <T>(url: string, body: {}) =>
    axios.post<T>(url, body).then(responseBody),
  put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
  del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
};

/*---------------Product API---------*/
const Product = {
  search: (
    params: URLSearchParams,
    headers: {
      outletId: number;
    },
  ) =>
    axios
      .get<SearchProducts>(`/api/azure/search-product`, {
        params,
        headers: { outletId: String(headers.outletId) },
      })
      .then(responseBody),
  getAllCategory: (
    params: URLSearchParams,
    headers: {
      customerTypeId: number;
      customerOrgId: number;
      outletId: number;
    },
  ) =>
    axios
      .get<SearchProducts>(`/api/admin/salesApp/getAllCategory`, {
        params,
        headers: {
          customerTypeId: String(headers.customerTypeId),
          customerOrgId: String(headers.customerOrgId),
          outletId: String(headers.outletId),
        },
      })
      .then(responseBody),
  getAllSubCategory: (
    params: URLSearchParams,
    headers: {
      customerTypeId: number;
      customerOrgId: number;
      outletId: number;
    },
  ) =>
    axios
      .get<SearchProducts>(`/api/admin/salesApp/getAllSubCategory`, {
        params,
        headers: {
          customerTypeId: String(headers.customerTypeId),
          customerOrgId: String(headers.customerOrgId),
          outletId: String(headers.outletId),
        },
      })
      .then(responseBody),
  autocomplete: (
    params: URLSearchParams,
    headers: {
      customerTypeId: number;
      customerOrgId: number;
      outletId: number;
    },
  ) =>
    axios
      .get<AutocompleteResponse>(`/api/azure/autocomplete`, {
        params,
        headers: {
          customerTypeId: String(headers.customerTypeId),
          customerOrgId: String(headers.customerOrgId),
          outletId: String(headers.outletId),
        },
      })
      .then(responseBody),
};

/*---------------Customer API---------*/
const Customer = {
  list: (data: CustomerListFilter) =>
    requests.post<CustomerListResponse>(
      `customer-outlet/get-by-outlet-manager`,
      data,
    ),
  detail: (id: number) =>
    requests.get<CustomerDetailResponse>(`customer-outlet/${id}`),
};

/*---------------Orders API---------*/
const Orders = {
  list: (params: URLSearchParams) =>
    axios
      .get<OrderResponse>(`/api/admin/sales-person/get-all-customer-orders`, {
        params,
      })
      .then(responseBody),
  orderStats: (outletId: number) =>
    axios
      .get<CustomerOrderStatsReponse>(`/api/admin/customer_order_stats`, {
        params: { outletId: outletId },
      })
      .then(responseBody),
  orderDetail: (orderId: number) =>
    axios
      .get<OrderDetailApiResponse>("/api/admin/order_detail", {
        params: { id: orderId },
      })
      .then(responseBody),
  generate_order: (
    payload: GenerateOrderPayload,
    headers: {
      customerTypeId: number;
      customerOrgId: number;
      outletId: number;
    },
  ) =>
    axios
      .post<GenerateOrderApiResponse>(
        "/api/admin/enterprise/generate_order",
        payload,
        {
          headers: {
            customerTypeId: String(headers.customerTypeId),
            customerOrgId: String(headers.customerOrgId),
            outletId: String(headers.outletId),
          },
        },
      )
      .then(responseBody),
  reorder: (
    payload: ReorderPayload,
    headers: {
      customerTypeId: number;
      customerOrgId: number;
      outletId: number;
    },
  ) =>
    axios
      .post<ReorderResponse>("/api/admin/reorder", payload, {
        headers: {
          customerTypeId: String(headers.customerTypeId),
          customerOrgId: String(headers.customerOrgId),
          outletId: String(headers.outletId),
        },
      })
      .then(responseBody),
  sendRetentionOrderPlaced: (payload: any) =>
    axios
      .post(`${process.env.REACT_APP_CRM_BASE_URL}/api/v1/retention-customers/order-placed`, payload)
      .then(responseBody),
};

/*---------------User API---------*/
const User = {
  login: (payload: Login) =>
    requests.post<LoginAuthWrapper>(`/api/admin/login`, payload),
  getRefreshToken: (payload: RefreshTokenPayload) => {
    const currentToken = store.commonStore.token;
    return axiosPublic
      .post<RefreshToken>("/api/admin/token_refresh", payload, {
        headers: currentToken
          ? { Authorization: `Bearer ${currentToken}` }
          : {},
      })
      .then((res) => res.data);
  },
  getAdminProfile: (params: URLSearchParams) =>
    axios
      .get<AdminProfileApiResponse>("/api/admin/profile", { params })
      .then(responseBody),
  verifyOtp: (keycloakToken: string) =>
    axios
      .post<SystemUser>(
        "/api/admin/verify-admin-otp-sales-app",
        {},
        {
          headers: {
            token: keycloakToken,
          },
        },
      )
      .then(responseBody),
  getRoleMenuAction: (roleId: number) =>
    axios
      .get<RoleMenuActionResponse>(`/api/admin/role_menu_action/${roleId}`, {
        headers: {
          pagePath: "/otp-verify",
        },
      })
      .then(responseBody),
  getKamStats: () =>
    axios
      .get<KamStatResponse[]>('/api/admin/manage/user/kams-stats').then(responseBody),
};

/*---------------Dashboard API---------*/
const Dashboard = {
  getTopDashboardItems: () =>
    requests.get<TopDashboardResponse>(
      `/api/admin/sales-person/dashboard-metrics`,
    ),
  getDailyMetrics: () =>
    requests.get<DailyMetricsResponse>(`/api/admin/sales-person/daily-metrics`),
  getCurrentMonthMetrics: () =>
    requests.get<DashboardMetricResponse>(
      `/api/admin/sales-person/current-month-metrics`,
    ),
  getPrevMonthMetrics: () =>
    requests.get<PreviousMonthMetricResponse>(
      `/api/admin/sales-person/previous-month-metrics`,
    ),
};

/*---------------Cart API---------*/
const Cart = {
  addItem: (body: AddToCartReqParams) =>
    requests.post<AddToCartRes>(`/api/admin/enterprise/add_to_cart`, body),
  clearCart: (payload: ClearCartReqParams, headers: { outletId: number }) =>
    axios
      .post(`/api/admin/enterprise/clear_cart`, payload, {
        headers: {
          outletId: String(headers.outletId),
        },
      })
      .then(responseBody),
  detail: (params: URLSearchParams) =>
    axios
      .get<CartDetails>(`/api/admin/enterprise/cart_detail`, {
        params,
      })
      .then(responseBody),

  validateQuantity: (
    payload: CartQuantityValidationPayload,
    headers: CartQuantityValidationHeaders,
  ) =>
    axios
      .post<CartQuantityValidationResponse>(
        "/add_to_cart_validator/validate",
        payload,
        {
          headers: {
            outletId: String(headers.outletId),
            customerTypeId: String(headers.customerTypeId),
            customerOrgId: String(headers.customerOrgId),
          },
        },
      )
      .then(responseBody),

  /*---------------Coupon API---------*/
  fetchApplicableCoupons: (
    params: URLSearchParams,
    headers: { customerTypeId: number; outletId: number },
  ) =>
    axios
      .get<ApplicableCouponsApiResponse>(
        "/customer-coupon/enterprise/applicable-coupons",
        {
          params,
          headers: {
            customerTypeId: String(headers.customerTypeId),
            outletId: String(headers.outletId),
          },
        },
      )
      .then(responseBody),
  verifyCoupon: (
    params: URLSearchParams,
    headers: { customerTypeId: number; outletId: number },
  ) =>
    axios
      .get<VerifyCouponApiResponse>("/customer-coupon/verify-coupon", {
        params,
        headers: {
          customerTypeId: String(headers.customerTypeId),
          outletId: String(headers.outletId),
        },
      })
      .then(responseBody),

  /*---------------Delivery Slots API---------*/
  fetchDeliverySlots: (
    params: URLSearchParams,
    headers: {
      customerTypeId: number;
      customerOrgId: number;
      outletId: number;
    },
  ) =>
    axios
      .get<GetAllDeliverySlotsApiResponse>("/service-area-polygons/get-slots", {
        params,
        headers: {
          customerTypeId: String(headers.customerTypeId),
          customerOrgId: String(headers.customerOrgId),
          outletId: String(headers.outletId),
        },
      })
      .then(responseBody),

  quickDeliveryConfiguration: (params: URLSearchParams) =>
    axios
      .get<QuickDeliveryConfigurationApiResponse>(
        "/api/admin/quickDeliveryConfigurationSalesApp",
        { params },
      )
      .then(responseBody),

  uploadExcel: (
    file: File,
    warehouseId: number,
    businessTypeId: number,
    outletId: number,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("warehouseId", String(warehouseId));
    formData.append("businessTypeId", String(businessTypeId));
    formData.append("outletId", String(outletId));

    return axios
      .post<ExcelUploadResponse>("/api/admin/addtoCartViaExcel", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(responseBody);
  },

  postCartDeliveryTime: (
    payload: { warehouse_id: number; order_delivery_type: number },
    headers: { outletId: number },
  ) =>
    axios
      .post<any>("/warehouse/capacity/daily/details/bulk-check", payload, {
        headers: {
          outletId: String(headers.outletId),
        },
      })
      .then(responseBody),
};

/*---------------Fetch WarehouseId API---------*/
const Warehouse = {
  getWarehouseId: (
    lat: number,
    long: number,
    customerTypeId: number,
    outletId: number,
  ) =>
    axios
      .get<GetWareHouseResponse>(`/service-area-polygons/check-location`, {
        params: { latitude: lat, longitude: long, customerTypeId },
        headers: { outletId: String(outletId) },
      })
      .then(responseBody),
};

/*---------------Onboarding API---------*/
const Onboarding = {
  getAllState: () =>
    axios
      .get<GetAllStateApiResponse>("/api/user/getAllState")
      .then(responseBody),
  getAllCities: (stateId: number) =>
    axios
      .get<GetAllCitiesApiResponse>(`/api/user/getCityFromState/${stateId}`)
      .then(responseBody),
  getCustomerTypes: () =>
    axios
      .get<CustomerTypeResponse>("/api/admin/business_types", {
        headers: {
          menuId: store.commonStore.menuId,
        },
      })
      .then(responseBody),
  getBusinessTypes: (addressType: string) =>
    axios
      .get<BusinessTypeResponse>("/settings-metadata/find-by-name", {
        params: {
          names: addressType,
        },
      })
      .then(responseBody),
  addOutlet: (data: any) =>
    axios
      .post<AddOutletResponse>(`/customer-outlet`, data, {
        headers: {
          menuId: store.commonStore.menuId,
        },
      })
      .then(responseBody),
  uploadAttachments: (outletId: number, formData: FormData) =>
    axios
      .put(`/customer-outlet/${outletId}/attachments`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          menuId: store.commonStore.menuId,
        },
      })
      .then(responseBody),
  addOrganisation: (data: any) =>
    axios
      .post(`/organisation`, data, {
        headers: {
          menuId: store.commonStore.menuId,
        },
      })
      .then(responseBody),
  uploadOrganisationImage: (formData: FormData) =>
    axios
      .post(`/organisation/upload-image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          menuId: store.commonStore.menuId,
        },
      })
      .then(responseBody),
  getOutletByOrgId: (orgId: number) =>
    axios
      .get<OutletByOrgResponse>(`/customer-outlet/get-by-org-id/${orgId}`, {
        headers: {
          menuId: store.commonStore.menuId,
        },
      })
      .then(responseBody),
  updateOutlet: (outletId: number, data: any) =>
    axios
      .patch<UpdateOutletResponse>(`/customer-outlet/${outletId}`, data, {
        headers: {
          menuId: store.commonStore.menuId,
        },
      })
      .then(responseBody),
  getCrmOnboardCustomers: () =>
    axios
      .get(`${process.env.REACT_APP_CRM_BASE_URL}/api/v1/sales/onboardCustomers`)
      .then(responseBody),
};

/*---------------Places/Location API---------*/
const Places = {
  autocomplete: (input: string) =>
    axios
      .post<LocationAutocompleteResponse>("/places/autocomplete", { input })
      .then(responseBody),
  details: (placeId: string) =>
    axios
      .get<LocationDetailsResponse>(`/places/details?placeId=${placeId}`)
      .then(responseBody),
};

const Payment = {
  getRemainingPodLimit: (
    orgId: number,
    warehouseId: number,
    headers: { customerTypeId: number; customerOrgId: number; outletId: number },
  ) =>
    axios
      .get<any>(`/api/${orgId}/remaining-pod-limit`, {
        params: { warehouseId },
        headers: {
          customerTypeId: String(headers.customerTypeId),
          customerOrgId: String(headers.customerOrgId),
          outletId: String(headers.outletId),
        },
      })
      .then(responseBody),
  getPaymentMethods: (
    orgId: number,
  ) =>
    axios
      .get<any>(`/payment-methods/org/${orgId}/sales`)
      .then(responseBody),
};

const agent = {
  Product,
  Customer,
  User,
  Cart,
  Orders,
  Dashboard,
  Warehouse,
  Onboarding,
  Places,
  Payment,
};

export default agent;
